import React, { useState } from 'react';
import axios from 'axios';
import { ArrowRight, Loader2, ShieldCheck } from 'lucide-react';
import { BetaCheck } from './BetaAgreement';

const API = process.env.REACT_APP_BACKEND_URL;
const INPUT = 'w-full h-12 px-4 rounded-xl border border-[#D9E2EB] bg-white text-base text-[#0E2340] placeholder:text-[#7a8a9c] focus:outline-none focus:ring-2 focus:ring-[#1A7FD6]/40 focus:border-[#1A7FD6]';
const FIELDS = [
  ['fullName', 'Your full name (e.g. Jane Smith, DDS) *', 'text'],
  ['practiceName', 'Practice name *', 'text'],
  ['specialty', 'Your specialty (general, perio, ortho…) *', 'text'],
  ['cityState', 'Town and state *', 'text'],
  ['email', 'Email *', 'email'],
  ['phone', 'Cell phone (optional)', 'tel'],
  ['website', 'Practice website (optional)', 'text'],
];
const EMPTY = { fullName: '', practiceName: '', specialty: '', cityState: '', email: '', phone: '', website: '', currentComms: '', whyPick: '',
  videoOk: false, publicReviewOk: false, agreeAll: false, photoGuarantee: false, publishPermission: false, signature: '' };

export const BetaForm = ({ onDone }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const norm = (s) => s.toLowerCase().split(/\s+/).filter(Boolean).join(' ');
  const ready = f.fullName && f.practiceName && f.specialty && f.cityState && f.email && f.agreeAll && f.photoGuarantee && f.publishPermission && f.signature;

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (norm(f.signature) !== norm(f.fullName)) { setErr('Your signature must match your full name exactly.'); return; }
    setBusy(true);
    try {
      const tracking = Object.fromEntries([...new URLSearchParams(window.location.search)].filter(([k]) => /^(utm_|gclid|fbclid)/.test(k)));
      await axios.post(`${API}/api/beta/apply`, { ...f, tracking });
      onDone(f.email);
    } catch (ex) {
      const d = ex.response?.data?.detail;
      setErr(typeof d === 'string' ? d : 'Please check the form — something is missing or not valid.');
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-12" data-testid="beta-form">
      <h2 className="beta-display text-2xl sm:text-3xl font-extrabold text-[#0E2340]">Your practice</h2>
      <div className="mt-5 grid sm:grid-cols-2 gap-3">
        {FIELDS.map(([k, ph, type]) => (
          <input key={k} type={type} placeholder={ph} value={f[k]} onChange={(e) => set(k)(e.target.value)} className={INPUT} data-testid={`beta-input-${k}`} />
        ))}
      </div>
      <textarea placeholder="How do you send patients post-op instructions and consents today? (optional, but it helps us pick)" value={f.currentComms}
        onChange={(e) => set('currentComms')(e.target.value)} rows={3} className={`${INPUT} h-auto py-3 mt-3`} data-testid="beta-input-currentComms" />
      <textarea placeholder="Why should we pick your practice? (optional)" value={f.whyPick} onChange={(e) => set('whyPick')(e.target.value)}
        rows={3} className={`${INPUT} h-auto py-3 mt-3`} data-testid="beta-input-whyPick" />
      <div className="mt-4 space-y-3">
        <BetaCheck id="video" checked={f.videoOk} onChange={set('videoOk')}>I'd be willing to record a short video testimonial too (not required)</BetaCheck>
        <BetaCheck id="review" checked={f.publicReviewOk} onChange={set('publicReviewOk')}>I'll leave a 5-star review on Google or Facebook if I'm happy — that makes PractCom free for life (not required)</BetaCheck>
      </div>

      <div className="mt-10 space-y-3">
        <BetaCheck id="agree" checked={f.agreeAll} onChange={set('agreeAll')}>I've read every point of the agreement above and I agree to all of them.</BetaCheck>
        <BetaCheck id="photo" checked={f.photoGuarantee} onChange={set('photoGuarantee')} highlight>
          <strong>I guarantee a photo of myself</strong> with my testimonial — my own face, not a logo or a stock picture. I understand the free account depends on it.
        </BetaCheck>
        <BetaCheck id="publish" checked={f.publishPermission} onChange={set('publishPermission')}>
          I give PractCom permission to publish my testimonial, my photo, my name and credentials, practice name and town.
        </BetaCheck>
      </div>

      <p className="mt-8 font-mono text-xs tracking-[.14em] uppercase text-[#0E2340] font-medium">Signature — type your full name</p>
      <input value={f.signature} onChange={(e) => set('signature')(e.target.value)} placeholder="Type your full name to sign" className={`${INPUT} mt-2 italic`} data-testid="beta-input-signature" />
      <p className="mt-2 text-sm text-[#46586E] flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" />Dated and stored with the exact terms shown above when you sign.</p>

      {err && <p className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="beta-error">{err}</p>}
      <button type="submit" disabled={!ready || busy}
        className="mt-6 inline-flex items-center gap-2 h-14 px-8 rounded-xl bg-[#0A7A55] hover:bg-[#086646] text-white text-lg font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid="beta-submit-btn">
        {busy ? <><Loader2 className="w-5 h-5 animate-spin" /> Creating your account…</> : <>Sign and join the beta <ArrowRight className="w-5 h-5" /></>}
      </button>
      <p className="mt-4 text-sm text-[#46586E]">
        We only use your details to run the beta and to get back to you. Nothing is shared or sold.{' '}
        <a href="/landing" className="underline" data-testid="beta-see-practcom-link">See what you'd be testing</a> ·{' '}
        <a href="/practcom/terms" className="underline">Terms</a> · <a href="/practcom/privacy" className="underline">Privacy</a>
      </p>
    </form>
  );
};
